'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { FindingEditSignalCollection } from './findingEditForm.types';
import {
  Badge,
  Combobox,
  IconButton,
  Input,
  MetaDefault,
  formatCategoryForDisplay,
  formatFindingStatusForDisplay,
} from '@terra/ui-design-system';
import { ArrowUpDownIcon, Cancel01Icon, Tick01Icon } from '@terra/ui-icons';
import { HStack, VStack } from '@terra/ui-styled-system';
import type {
  FindingStatusType,
  SignalCategoryType,
} from '@terra/shared-schemas-orchestrator';

type FindingEditSignalComboboxProps = {
  collection: FindingEditSignalCollection;
  selectedValue: string | null;
  isSearching: boolean;
  onValueChange: (signalId: string | null) => void;
  onInputValueChange: (query: string) => void;
};

export function FindingEditSignalCombobox({
  collection,
  selectedValue,
  isSearching,
  onValueChange,
  onInputValueChange,
}: FindingEditSignalComboboxProps) {
  const t = useTranslations(
    'designSystem.findings.editDrawer.sections.assignment.fields.signalId',
  );
  const selectedItem = selectedValue
    ? collection.items.find((item) => item.value === selectedValue)
    : undefined;
  const [inputValue, setInputValue] = useState(selectedItem?.label ?? '');
  const previousSelectedValueRef = useRef<string | null>(selectedValue);

  useEffect(() => {
    const previousSelectedValue = previousSelectedValueRef.current;
    previousSelectedValueRef.current = selectedValue;

    if (!selectedValue) {
      setInputValue('');
      return;
    }

    if (previousSelectedValue !== selectedValue) {
      setInputValue(selectedItem?.label ?? '');
      return;
    }

    if (!selectedItem?.label) {
      return;
    }

    setInputValue((current) => {
      if (
        current === '' ||
        current === selectedValue.slice(0, 8) ||
        current === selectedItem.label
      ) {
        return selectedItem.label;
      }

      return current;
    });
  }, [selectedValue, selectedItem?.label]);

  return (
    <HStack gap={1} alignItems="center" w="full">
      <Combobox.Root
        collection={collection}
        value={selectedValue ? [selectedValue] : []}
        inputValue={inputValue}
        onValueChange={(details) => {
          const nextSignalId = details.value[0];

          if (!nextSignalId) {
            return;
          }

          onValueChange(nextSignalId);
        }}
        onInputValueChange={(details) => {
          setInputValue(details.inputValue);
          onInputValueChange(details.inputValue);
        }}
        size="sm"
        flex={1}
        minW={0}
        w="full"
      >
        <Combobox.Control>
          <Combobox.Input placeholder={t('placeholder')} asChild>
            <Input
              size="sm"
              minW={0}
              pr="11"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            />
          </Combobox.Input>
          <Combobox.Trigger asChild>
            <IconButton variant="link" aria-label={t('label')} size="xs">
              <ArrowUpDownIcon size={14} />
            </IconButton>
          </Combobox.Trigger>
        </Combobox.Control>
        <Combobox.Positioner>
          <Combobox.Content maxH="300px" overflowY="auto">
            <Combobox.List>
              {isSearching ? (
                <MetaDefault p={2} textAlign="center" color="fg.muted">
                  {t('searching')}
                </MetaDefault>
              ) : collection.items.length > 0 ? (
                collection.items.map((item) => (
                  <Combobox.Item
                    key={item.value}
                    item={item}
                    height="auto"
                    py={2.5}
                  >
                    <VStack gap={1} alignItems="flex-start" flex={1} minW={0}>
                      <HStack gap={1}>
                        {item.status && (
                          <Badge size="sm" variant="outline">
                            {formatFindingStatusForDisplay(
                              item.status as FindingStatusType,
                            )}
                          </Badge>
                        )}
                        {item.category && (
                          <Badge size="sm" variant="fill" colorPalette="gray">
                            {formatCategoryForDisplay(
                              item.category as SignalCategoryType,
                            )}
                          </Badge>
                        )}
                      </HStack>
                      <Combobox.ItemText
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        w="full"
                      >
                        {item.label}
                      </Combobox.ItemText>
                    </VStack>
                    <Combobox.ItemIndicator>
                      <Tick01Icon size={14} />
                    </Combobox.ItemIndicator>
                  </Combobox.Item>
                ))
              ) : (
                <MetaDefault p={2} textAlign="center" color="fg.muted">
                  {t('typeToSearch')}
                </MetaDefault>
              )}
            </Combobox.List>
          </Combobox.Content>
        </Combobox.Positioner>
      </Combobox.Root>
      {selectedValue && (
        <IconButton
          variant="outline"
          size="xs"
          flexShrink={0}
          onClick={() => {
            setInputValue('');
            onValueChange(null);
          }}
          aria-label={t('clearAriaLabel')}
        >
          <Cancel01Icon size={14} />
        </IconButton>
      )}
    </HStack>
  );
}
